import { ChevronLeft, FileText, ShieldCheck, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PolicyContact from './PolicyContact';

const EFFECTIVE_DATE = "12 August 2026";
const UPDATED_DATE = "12 August 2026";

export default function TermsAndConditionsPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-[#fcfaf9] pb-16">
      <section className="border-b border-[#eadde1] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-primary-800"
          >
            <ChevronLeft size={17} />
            Back
          </button>

          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary-800">
                CHARMS Policies
              </p>

              <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                Terms & Conditions
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                The terms that govern your use of the CHARMS website, customer
                applications and online shopping services.
              </p>
            </div>

            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-800 text-white shadow-lg shadow-primary-900/20">
              <FileText size={27} />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 border-t border-[#eadde1] pt-5 text-xs font-medium text-slate-500">
            <span>Effective date: {EFFECTIVE_DATE}</span>
            <span>Last updated: {UPDATED_DATE}</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="h-fit rounded-2xl border border-[#eadde1] bg-white p-5 lg:sticky lg:top-48">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-800">
              At a glance
            </p>

            <div className="mt-5 space-y-5">
              <SummaryItem
                icon={<ShoppingBag size={17} />}
                title="Prepaid shopping"
                text="Orders are accepted after payment verification and stock review."
              />

              <SummaryItem
                icon={<ShieldCheck size={17} />}
                title="Protected rights"
                text="These Terms do not limit consumer rights that law protects."
              />

              <SummaryItem
                icon={<FileText size={17} />}
                title="Related policies"
                text="Shipping, returns and loyalty policies form part of these Terms."
              />
            </div>

            <div className="mt-6 space-y-3 border-t border-[#eadde1] pt-5">
              <button
                type="button"
                onClick={() => navigate("/shippingpolicy")}
                className="block text-left text-sm font-semibold text-primary-800 transition hover:underline"
              >
                Shipping Policy
              </button>

              <button
                type="button"
                onClick={() => navigate("/refundpolicy")}
                className="block text-left text-sm font-semibold text-primary-800 transition hover:underline"
              >
                Returns & Exchanges Policy
              </button>

              <button
                type="button"
                onClick={() => navigate("/loyaltyterms")}
                className="block text-left text-sm font-semibold text-primary-800 transition hover:underline"
              >
                Loyalty Programme Terms
              </button>
            </div>
          </aside>

          <article className="rounded-2xl border border-[#eadde1] bg-white p-6 sm:p-10">
            <p className="text-sm leading-7 text-slate-600">
              These Terms and Conditions (“Terms”) govern your use of{" "}
              <a
                href="https://charmscollections.in"
                className="font-semibold text-primary-800 underline underline-offset-4"
              >
                charmscollections.in
              </a>
              , related customer applications and our online shopping services
              (together, the “Services”).
            </p>

            <p className="mt-4 text-sm leading-7 text-slate-600">
              The Services are operated by Charms Collections Pvt. Ltd.
              (“CHARMS”, “we”, “us” or “our”). By accessing or using the
              Services, you agree to these Terms.
            </p>

            <div className="mt-10 space-y-10">
              <PolicySection title="1. Eligibility and Accounts">
                <p>
                  You must provide accurate and current information when
                  creating an account or placing an order. You are responsible
                  for safeguarding your credentials and for activity carried out
                  through your account.
                </p>

                <p className="mt-4">
                  Please notify us promptly if you suspect unauthorised use of
                  your account.
                </p>
              </PolicySection>

              <PolicySection title="2. Products and Availability">
                <p>
                  We make reasonable efforts to display products, colours,
                  sizes, descriptions, images, pricing and availability
                  accurately. Display settings and photography can affect how
                  colours appear.
                </p>

                <p className="mt-4">
                  Product availability is not guaranteed. We may limit
                  quantities, discontinue products, correct inadvertent errors
                  or cancel orders where necessary.
                </p>
              </PolicySection>

              <PolicySection title="3. Prices, Taxes and Promotions">
                <p>
                  Prices, applicable taxes, offers and delivery charges are
                  displayed at checkout before payment. Prices and promotions
                  may change without notice.
                </p>

                <p className="mt-4">
                  A confirmed order is generally charged at the amount shown at
                  the time of successful checkout, subject to correction of
                  obvious errors, fraud review or legal requirements.
                </p>

                <p className="mt-4">
                  Promotion codes, coupons, loyalty offers and brand discounts
                  are subject to their own stated conditions, availability,
                  exclusions, dates and verification. They cannot be exchanged
                  for cash unless required by law.
                </p>
              </PolicySection>

              <PolicySection title="4. Payments and Order Acceptance">
                <p>
                  Online orders are prepaid. Payment processing is handled by
                  authorised third-party payment providers, including HDFC
                  SmartGateway where selected.
                </p>

                <p className="mt-4">
                  Submitting payment details or receiving an acknowledgement
                  does not itself mean that CHARMS has accepted your order. We
                  accept an order only after payment is successfully verified,
                  the order is confirmed and the item is available for
                  fulfilment.
                </p>

                <p className="mt-4">
                  We may decline, suspend or cancel an order before dispatch for
                  reasons such as failed or unverified payment, pricing or stock
                  error, suspected fraud, prohibited resale, delivery
                  restrictions or a violation of these Terms.
                </p>

                <p className="mt-4">
                  Where payment has been captured for an order we cannot
                  fulfil, we will arrange the applicable refund.
                </p>
              </PolicySection>

              <PolicySection title="5. Delivery, Returns and Exchanges">
                <p>
                  Delivery is governed by our Shipping Policy. Returns, refunds
                  and exchanges are governed by our Returns, Refunds and
                  Exchanges Policy. These policies form part of these Terms.
                </p>

                <p className="mt-4">
                  Nothing in these policies limits statutory consumer rights.
                </p>
              </PolicySection>

              <PolicySection title="6. CHARMS Loyalty Programme">
                <p>
                  If you participate in the CHARMS Loyalty Programme, the
                  Loyalty Programme Terms and Conditions also apply.
                </p>

                <p className="mt-4">
                  Points are a conditional programme benefit, have no cash value
                  except where law requires otherwise, and may be adjusted or
                  reversed for cancellations, returns, refunds, error
                  correction or misuse.
                </p>
              </PolicySection>

              <PolicySection title="7. Acceptable Use">
                <p>You must not misuse the Services. This includes:</p>

                <ul className="mt-4 list-disc space-y-2 pl-5">
                  <li>Interfering with the security or operation of the Services.</li>
                  <li>Attempting unauthorised access to any account or system.</li>
                  <li>
                    Using automated means to scrape the Services or place orders
                    without permission.
                  </li>
                  <li>Submitting false information.</li>
                  <li>Misusing promotions, coupons or loyalty points.</li>
                  <li>Using the Services for any unlawful purpose.</li>
                </ul>
              </PolicySection>

              <PolicySection title="8. Intellectual Property">
                <p>
                  The Services, including their content, design, software,
                  trademarks, logos, photographs and text, are owned by or
                  licensed to CHARMS or the relevant rights holders.
                </p>

                <p className="mt-4">
                  You may use the Services for personal, non-commercial
                  shopping only. No rights are transferred except as expressly
                  stated.
                </p>
              </PolicySection>

              <PolicySection title="9. Third-Party Services">
                <p>
                  The Services may include links, payment systems, delivery
                  services or other third-party offerings. Their terms and
                  privacy practices apply to their own services.
                </p>

                <p className="mt-4">
                  CHARMS is not responsible for third-party content or services
                  except to the extent required by law.
                </p>
              </PolicySection>

              <PolicySection title="10. Disclaimers and Liability">
                <p>
                  We aim to keep the Services accurate, secure and available,
                  but do not promise uninterrupted or error-free operation.
                </p>

                <p className="mt-4">
                  To the maximum extent permitted by law, CHARMS will not be
                  liable for indirect, incidental, special or consequential
                  losses arising from use of the Services.
                </p>

                <p className="mt-4">
                  This clause does not exclude liability that cannot lawfully be
                  excluded, including applicable consumer rights.
                </p>
              </PolicySection>

              <PolicySection title="11. Governing Law and Disputes">
                <p>
                  These Terms are governed by the laws of India. Courts at
                  Patiala, Punjab will have jurisdiction, subject to any
                  mandatory consumer-law forum or remedy that applies to you.
                </p>
              </PolicySection>

              <PolicySection title="12. Changes to These Terms">
                <p>
                  We may update these Terms from time to time. The updated
                  version will be posted on the Services with an updated
                  effective date.
                </p>

                <p className="mt-4">
                  Continued use after an update takes effect means you accept
                  the updated Terms, where permitted by law.
                </p>
              </PolicySection>

              <PolicySection title="13. Contact and Grievances">
                <p>
                  For questions, concerns or grievances, please use the
                  customer-support details published on the CHARMS website.
                </p>

                <div className="mt-5 rounded-xl bg-[#faf7f5] p-5 text-sm leading-7 text-slate-600">
                  <PolicyContact />
                </div>
              </PolicySection>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}

function PolicySection({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
        {title}
      </h2>

      <div className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px]">
        {children}
      </div>
    </section>
  );
}

function SummaryItem({ icon, title, text }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-800">
        {icon}
      </span>

      <div>
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">{text}</p>
      </div>
    </div>
  );
}
